import React from "react";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import BlockList from "components/FormerEditor/BlockList";
import BlockSettings from "components/FormerEditor/BlockSettings";
import Preview from "components/FormerEditor/Preview";
import AddEntityBlockItem from "components/FormerEditor/AddEntityBlockItem";
import ThemeBlockItem from "components/FormerEditor/ThemeBlockItem";
import PreviewLoader from "components/FormerEditor/EditorLoader/PreviewLoader";
import BlockPicker from "components/FormerEditor/BlockPicker";
import PageTheme from "components/FormerEditor/PageTheme";
import {
  BLOCK_LIST_WIDTH,
  BUILDER_INTENT,
  BLOCK_SETTINGS_WIDTH,
  BLOCK_REGISTRY
} from "utils/constants/builder";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import { useModelQueryId } from "utils/hooks";
import { PRODUCT } from "utils/constants/models";
import { usePrevPathFragment } from "utils/hooks/hub";
import { isModelCreateRoute } from "utils/route";
import { useRouter } from "next/router";
import EditProduct from "components/ProductForm/Edit";
import Overlay from "components/Overlay";
import { DEFAULT_OVERLAY_STYLES } from "utils/constants/overlay";
import { NAV_BAR } from "utils/styles";

const PANEL_INTENT_MAP = {
  [BUILDER_INTENT.ENTITY_ADD]: BlockPicker,
  [BUILDER_INTENT.ENTITY_SETTINGS]: BlockSettings,
  [BUILDER_INTENT.PAGE_THEME]: PageTheme
};

const PageBuilder = () => {
  const { values } = useFormikContext();

  const backPath = usePrevPathFragment(2);
  const showProduct = useModelQueryId(PRODUCT);

  const { asPath, replace } = useRouter();
  const isCreateRoute = isModelCreateRoute(PRODUCT, asPath);
  const showOverlay = isCreateRoute || showProduct;
  let overlayContent;
  if (showProduct) {
    overlayContent = <EditProduct backAction={{ tooltip: "" }} />;
  }

  const { appLoading } = useSelector((state) => {
    return {
      appLoading: Boolean(state.getIn(["app", "isLoading"]))
    };
  });

  let panel;
  let panelBlock;
  const blockSettingsVisible = get(values, STATE_KEYS.EDITOR.VISIBILITY_PANEL);
  if (blockSettingsVisible) {
    const intentKey = get(values, STATE_KEYS.EDITOR.INTENT);
    const IntentComp = PANEL_INTENT_MAP[intentKey];
    if (IntentComp) {
      panel = <IntentComp key={intentKey} />;
    }
    panelBlock = (
      <div
        id="panel-section-menu"
        className="w-100 flex flex-column flex-shrink-0 bg-content--secondary bh--inset overflow-hidden"
        style={{ width: BLOCK_SETTINGS_WIDTH }}
      >
        {panel}
      </div>
    );
  }

  /**
   * NOTE:
   * - section nav is a sibling to this component
   * - the width of it is used for content calculation here
   */
  const blockControlsWidth = blockSettingsVisible ? NAV_BAR : BLOCK_LIST_WIDTH;
  return (
    <div className="flex flex-row h-100 w-100">
      <div
        style={{ width: blockControlsWidth }}
        className={"flex flex-column fl h-100 relative"}
      >
        <AddEntityBlockItem name={STATE_KEYS.EDITOR.ACTIVE_BLOCK} />
        <ThemeBlockItem name={STATE_KEYS.EDITOR.ACTIVE_BLOCK} />
        <BlockList name={STATE_KEYS.EDITOR.ACTIVE_BLOCK} />
      </div>
      <div
        className="flex fl h-100 bg--content-secondary"
        style={{
          /**
           * Note: leaving previous configuration here while determining if layout styling bug is a regression
           * - Can be removed if all seems ok
           * width: `calc(100% - ${BLOCK_LIST_WIDTH + BLOCK_SECTION_NAV}px)`
           */
          width: `calc(100% - ${blockControlsWidth}px)`
        }}
      >
        {panelBlock}
        <div
          id={BLOCK_REGISTRY.PAGE_PREVIEW}
          className="w-100 bg-content--secondary overflow-hidden"
        >
          {appLoading ? <PreviewLoader /> : <Preview />}
        </div>
      </div>
      {showOverlay && overlayContent && (
        <Overlay
          customStyles={DEFAULT_OVERLAY_STYLES}
          hideOverlay={() => {
            replace(backPath);
          }}
        >
          {overlayContent}
        </Overlay>
      )}
    </div>
  );
};

export default PageBuilder;
